import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { AlertService } from '@context/frontend/alert';
import { FIREBASE_AUTH } from '@context/frontend/common';
import {
	ExternalAuthService,
	ExternalViewContextService,
	ExternalViewLookupService,
	ExternalViewRouteData,
} from '../services';

@Injectable({ providedIn: 'root' })
export class ExternalViewAuthGuard implements CanActivate {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly router = inject(Router);
	private readonly alert = inject(AlertService);
	private readonly externalAuthService = inject(ExternalAuthService);
	private readonly lookup = inject(ExternalViewLookupService);
	private readonly externalViewContext = inject(ExternalViewContextService);

	async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
		this.externalViewContext.clear();

		const externalId = route.paramMap.get('externalId');
		if (!externalId) {
			return this.redirectNotFound().then(() => false);
		}

		let link: ExternalViewRouteData | null;
		try {
			link = await this.lookup.lookupByExternalId(externalId);
		} catch (error) {
			console.error('External view lookup failed', error);
			return this.redirectNotFound().then(() => false);
		}

		this.externalViewContext.setResolved(link);

		const { organizationId, externalId: resolvedExternalId } = link;

		let user = this.auth.currentUser;

		if (user) {
			const tokenResult = await user.getIdTokenResult(true);
			const claims = tokenResult.claims;
			const claimsMatch =
				claims['role'] === 'share' &&
				claims['orgId'] === organizationId &&
				claims['shareExternalId'] === resolvedExternalId;

			if (!claimsMatch) {
				await this.externalAuthService.invalidateViewerSessionOnServer();
				await this.externalAuthService.signOut();
				user = this.auth.currentUser;
			}
		}

		if (!this.auth.currentUser) {
			const authed = await this.externalAuthService.authenticateForShare(
				organizationId,
				resolvedExternalId,
			);
			if (!authed) {
				return this.redirectNotFound().then(() => false);
			}
		}

		const authedUser = this.auth.currentUser;
		if (!authedUser) {
			return this.redirectNotFound().then(() => false);
		}

		let sessionOk = await this.externalAuthService.setupExternalSession(
			authedUser.uid,
			organizationId,
			resolvedExternalId,
		);

		if (!sessionOk) {
			const resetOk =
				await this.externalAuthService.resetAndAuthenticateForExternalView(
					organizationId,
					resolvedExternalId,
				);
			if (!resetOk) {
				return this.redirectSessionExpired().then(() => false);
			}
			const nextUser = this.auth.currentUser;
			if (!nextUser) {
				return this.redirectSessionExpired().then(() => false);
			}
			sessionOk = await this.externalAuthService.setupExternalSession(
				nextUser.uid,
				organizationId,
				resolvedExternalId,
			);
		}

		if (!sessionOk) {
			return this.redirectSessionExpired().then(() => false);
		}

		return true;
	}

	private async redirectNotFound(): Promise<void> {
		this.externalViewContext.clear();
		await this.router.navigate(['/']);
		this.alert.open('external-content-not-found').subscribe();
	}

	private async redirectSessionExpired(): Promise<void> {
		this.externalViewContext.clear();
		await this.externalAuthService.signOut();
		await this.router.navigate(['/'], {
			queryParams: {
				titleKey: 'external.viewer.session-expired.title',
				descriptionKey: 'external.viewer.session-expired.description',
			},
		});
	}
}
